//#region Imports
import { Auth } from 'aws-amplify';
import { Component, HostBinding, OnInit, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { VMOnlineLoginService } from './vmonline-login.service';
import { Message } from 'primeng/api';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Challenges } from './challenges';
import { CognitoUser } from '../cognito-model/cognito-user.model';
import { AuthService } from 'src/app/auth/auth.service';
import { DialogService } from 'primeng/dynamicdialog';
import { ForgotPasswordComponent } from '../forgot-password/forgot-password.component';

import { MessageComponent } from '../../controls/message/message.component';
import { InputTextComponent } from '../../controls/input-text/input-text.component';
import { ButtonComponent } from '../../controls/buttons/button/button.component';

//#endregion Imports

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.scss'],
    providers: [DialogService],
    imports: [
    MessageComponent,
    ReactiveFormsModule,
    InputTextComponent,
    ButtonComponent,
    RouterLink
],
})
export class LoginComponent implements OnInit {
	private router = inject(Router);
	private loginService = inject(VMOnlineLoginService);
	private authService = inject(AuthService);
	private route = inject(ActivatedRoute);
	private dialogService = inject(DialogService);

	@HostBinding('className') componentClass: string;

	//#region component declarations
	isProcessing = false;
	loginForm: UntypedFormGroup;
	messages: Message[];
	showForgetPassword = false;


	//#endregion component declarations

	//#region constructor
	constructor() {
		this.componentClass = 'main-container';

		this.loginForm = new UntypedFormGroup({
			username: new UntypedFormControl('', [Validators.required, Validators.maxLength(250)]),
			password: new UntypedFormControl('', [Validators.required, Validators.maxLength(99)]),
			phone: new UntypedFormControl(''),
		});
	}
	//#endregion constructor

	//#region oninit
	ngOnInit(): void {
		this.authService.configureAmplify();
		this.authService.returnToUrlAfterLogin ??= this.route.snapshot.queryParams['ReturnUrl'];

		this.setShowForgetPassword();
	}
	//#endregion oninit

	//#region component methods

	/** this api checks if we need to show the forgot password link to the user or not */
	setShowForgetPassword(): void {
		this.loginService.allowPasswordRetrieval().subscribe((response) => {
			this.showForgetPassword = response;
		});
	}

	signInFail(detail: string): void {
		this.isProcessing = false;
		this.messages = [];
		this.messages.push({ severity: 'error', summary: null, detail });
	}

	signIn(event: Event): void {
		event.preventDefault();

		if (this.loginForm.invalid) {
			return;
		}

		this.messages = [];
		this.isProcessing = true;

		Auth.signIn(this.loginForm.controls.username.value, this.loginForm.controls.password.value).then(
			(user) => {
				// storing the user object in the service, so that it
				// can be accessed by TOTP / SMS components
				this.loginService.cognitoUser = user;
				this.signInSuccess(user);
			},
			(error) => {
				if (error?.code === 'NotAuthorizedException' && error?.message === 'Access Token has been revoked') {
					this.signInFail('Something went wrong. Did you sign in or out in another session?');
					Auth.signOut({ global: true });
				} else if (error?.code === 'NotAuthorizedException' || error?.code === 'UserNotFoundException') {
					this.signInFail('Username or password is incorrect');
				} else if (error?.code === 'PasswordResetRequiredException') {
					this.resetPassword(true, this.loginForm.controls.username.value);
				} else {
					// Some firewalls and browser extensions may block cause
					// ERR_CERT_COMMON_NAME_INVALID errors to occur.
					this.signInFail(`Something went wrong. This may be an issue with your
firewall or a browser extension. Please check with your IT support first before contacting
VM Online Support.`);

					// Throwing error will cause the error to be logged
					throw new Error('Unknown sign-in error: ' + JSON.stringify(error));
				}
			}
		);
	}

	signInSuccess(user: CognitoUser & { challengeName: Challenges }): void {
		switch (user.challengeName) {
			case Challenges.CHALLENGE_NAME_NEW_PASSWORD_REQUIRED: {
				this.resetPassword(false, null);
				break;
			}
			case Challenges.CHALLENGE_NAME_SOFTWARE_TOKEN_MFA:
			case Challenges.CHALLENGE_NAME_SMS_MFA: {
				this.validateAuthenticationCode(user.challengeName);
				break;
			}
			default: {
				this.afterCognitoSignInSuccess(user);
				break;
			}
		}
	}

	validateAuthenticationCode(challengeName: Challenges): void {
		this.isProcessing = false;
		this.router.navigate(['/authenticate', { challengeName }]);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	setupTotpAuthenticator(user: CognitoUser & { challengeName: Challenges }): void {
		this.isProcessing = false;
		Auth.setupTOTP(user)
			.then((code) => {
				this.loginService.setupTotpCode = code;
				this.router.navigate(['/setup-totp']);
			})
			.catch(() => {
				this.signInFail('Something went wrong. Please contact support or try again later');
			});
	}

	resetPassword(isVerificationCodeRequired: boolean, username: string): void {
		this.isProcessing = false;

		if (isVerificationCodeRequired) {
			this.router.navigate(['/reset-password', { coderequired: true, username: username }]);
		} else {
			this.router.navigate(['/reset-password']);
		}
	}

	private afterCognitoSignInSuccess(user: CognitoUser & { challengeName: Challenges }): void {
		this.loginService.signInWithCognitoUser(user).subscribe((result) => {
			this.isProcessing = false;

			if (result.error) {
				this.signInFail(result.error);
			} else if (result.mfaSetupRequired) {
				this.setupTotpAuthenticator(user);
			}
		});
	}

	forgotPassword(): void {
		const ref = this.dialogService.open(ForgotPasswordComponent, {
			data: {},
			baseZIndex: 10000,
			closable: true,
			closeOnEscape: true,
			dismissableMask: true,
			showHeader: false,
			width: '445px',
		});

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		ref.onClose.subscribe((result: any) => {
			if (result) {
				this.resetPassword(true, result.username);
			}
			ref.destroy();
		});
	}

	isPasswordDisabled(): boolean {
		return !this.loginForm.get('username').value;
	}
}
